/**
 * @class ErrorBoundary
 * @extends {React.Component}
 * @description A React error boundary component that catches JavaScript errors anywhere in its child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 * It also reports errors to Sentry for monitoring.
 * 
 * @property {React.ReactNode} children - The components to render within the error boundary.
 * @property {React.ReactNode} [fallback] - Optional custom fallback UI to display when an error occurs.
 * 
 * @example
 * // Basic usage
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 * 
 * @example
 * // With custom fallback UI
 * <ErrorBoundary fallback={<div>Something went wrong</div>}>
 *   <MyComponent />
 * </ErrorBoundary>
 */

import React from 'react';
import styles from './styles.module.scss';
import NotificationEmptyMessage from 'src/legacy/images/notification_empty_message.svg';
import Actions from 'src/components/actionbar';
import * as Sentry from "@sentry/react";


class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this.renderErrorMessages = this.renderErrorMessages.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error(error, info);
    Sentry.captureException(error, { extra: info });
  }

  renderErrorMessages() {

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <div className={styles.errorContainer}>
        <div className={styles.innerContainer}>
          <img src={NotificationEmptyMessage} alt="error" />
          <h1 className={styles.title}>Ooops…something's wrong, but don't know what exactly.</h1>
          <p className={styles.subTitle}>For the time being, why don't you return to the content hub?.</p>
          <Actions.Button onClick={() => window.location.href = "/hub"} title='Go to content hub' />
        </div>
      </div>
    );
  }

  render() {

    if (this.state.hasError) {
      return this.renderErrorMessages();
    }

    return this.props.children;
  }
}

export default ErrorBoundary;